// src/components/AomiIntro.jsx
//
// Character introduction shown above the episode reader.
//
// Previously the cartoon page opened straight onto "Ep 1 / Ep 2 / Ep 3" — a
// visitor arriving from a QR code met a row of buttons with no idea whose
// story they were about to read. This gives the comic a doorway.

import React from "react";
import { imgurSize } from "../utils/imgur";

/* ── Images ──────────────────────────────────────────────────────────────────
   These must be DIRECT image URLs (i.imgur.com/<id>.<ext>), not Imgur page
   URLs (imgur.com/<id>) — the latter serve HTML and won't render in an <img>.
   Open the Imgur page, right-click the image, Copy Image Address.

   Check the extension: your artwork is .jpeg and the comic panels are .png,
   so don't assume.
   ------------------------------------------------------------------------ */
const AOMI_PORTRAIT = "https://i.imgur.com/Pyf9zaN.png";
const AOMI_SECONDARY = "https://i.imgur.com/JUjRuZu.png";

export default function AomiIntro({ onBeginReading }) {
  return (
    <section className="aomi-intro" aria-labelledby="aomi-heading">
      <div className="aomi-grid">
        {/* Portrait. Eager and high priority — it's the largest element above
            the fold on this page, so it's the LCP candidate. */}
        <figure className="aomi-portrait-figure">
          <img
            src={imgurSize(AOMI_PORTRAIT, "l")}
            alt="Aomi the Soulweaver — a figure in a navy hoodie with hazel green eyes"
            className="aomi-portrait"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </figure>

        <div className="aomi-text">
          <p className="aomi-eyebrow">Meet</p>

          <h2 id="aomi-heading" className="aomi-name">
            Aomi
            <span className="aomi-title">The Soulweaver</span>
          </h2>

          <p className="aomi-etymology">
            From <em>ao</em> — blue — and <em>mi</em> — soul, beauty.
          </p>

          <p className="aomi-essence">
            A modern ninja awakened by wonder, on a divine quest
            to transmute soul into story.
          </p>

          <dl className="aomi-facts">
            <div className="aomi-fact">
              <dt>Joy of Painting</dt>
              <dd>
                Through brushstrokes and paint I weave light into shadow,
                transforming trauma into tenderness. Each painting is both a
                weapon and a window — a glimpse into a memory, or a portal to
                healing.
              </dd>
            </div>

            <div className="aomi-fact">
              <dt>Superpower</dt>
              <dd>
                Inner ripples becoming outer resonance.
              </dd>
            </div>

            <div className="aomi-fact">
              <dt>Inner Minds Voice</dt>
              <dd>
                <strong>The Inner Sanctum</strong> — hidden in
                plain sight, nestled between worlds. Part art, part spirit.
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Second image, full width beneath the introduction. */}
      <figure className="aomi-secondary-figure">
        <img
          src={imgurSize(AOMI_SECONDARY, "h")}
          alt="Aomi the Soulweaver"
          className="aomi-secondary"
          loading="lazy"
          decoding="async"
        />
      </figure>

      {/* The structural glossary. Collapsed by default: it's world-building
          for the curious rather than something between a visitor and the
          comic. <details> gives keyboard and screen-reader support for free. */}
      <details className="aomi-structure">
        <summary>How The Living Tapestry is woven</summary>
        <dl className="aomi-structure-list">
          <div>
            <dt>Tapestry</dt>
            <dd>The whole world, evolving across years.</dd>
          </div>
          <div>
            <dt>Threads</dt>
            <dd>Yearly exhibitions — each alias a new level of growth.</dd>
          </div>
          <div>
            <dt>Episodes</dt>
            <dd>Cartoon strips and artworks. Transformative moments, portals.</dd>
          </div>
          <div>
            <dt>Characters</dt>
            <dd>
              Inner selves, archetypes and spirits met along the way — some
              mischievous, some guiding.
            </dd>
          </div>
        </dl>
      </details>

      {/* Returning readers shouldn't have to scroll past the introduction
          every visit. This skips them to the reader. */}
      <div className="aomi-cta">
        <button type="button" className="ep-continue" onClick={onBeginReading}>
          Begin reading ↓
        </button>
      </div>
    </section>
  );
}
